import { concat } from '@ember/helper';
import { or } from 'tio-ui/utilities';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { dropTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-ui/components/button';
import ChangeApprover from './my-documents/my-forms-table/change-approver';
import Chip from 'tio-ui/components/chip';
import Component from '@glimmer/component';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import embedPdf from 'tio-common/modifiers/embed-pdf';
import pslfApproverDisplayEmail from 'tio-common/helpers/pslf-approver-display-email';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import Popover from 'tio-ui/components/popover';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import Table from 'tio-ui/components/table';
import {
  PhCaretDown,
  PhCaretUp,
  PhDotsThreeVertical,
  PhEye,
  PhEyeSlash,
  PhPaperPlaneTilt,
  PhUserCheck,
  PhUserGear,
} from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import { INACTIVE_STATUSES } from 'tio-data/data/resources/pslf-form.constants';
import {
  cancelPslfFormRequest,
  notifyEmployerSignReminderRequest,
} from 'tio-data/requests/pslf-form-actions';
import { reloadRecord } from 'tio-data/requests/save-record';
import type { Store as StoreService } from '@warp-drive/core';
import type { TOC } from '@ember/component/template-only';
const INITIAL_LIMIT = 2;

// ---------------------------------------------------------------------------
// Row (template-only — stateless actions are handled by the parent)
// ---------------------------------------------------------------------------

function submittedToDoeDateDisplay(
  form: PslfFormModel
): 'date' | 'pending' | 'na' | 'sent_by_mail' {
  if (form.isPrintAndMail) return 'sent_by_mail';
  if (form.submittedAt) return 'date';
  if ((INACTIVE_STATUSES as readonly string[]).includes(form.status)) return 'na';
  return 'pending';
}

interface RowSignature {
  Args: {
    form: PslfFormModel;
    cellClass?: string;
    onPreview: (model: PslfFormModel) => void;
    onAction: (action: string, model: PslfFormModel) => void;
    menuOpenId: string | null;
    onToggleMenu: (id: string | null) => void;
    onCloseMenu: () => void;
  };
  Element: HTMLTableRowElement;
}

const BorrowerFormsRow: TOC<RowSignature> = <template>
  <tr ...attributes>
    {{! Date Borrower Signed }}
    {{#let @form.borrowerCompletedAt as |date|}}
      <td class={{@cellClass}} title={{date}}>
        {{#if @form.isOboForm}}
          <div class="tio-col-extra-tight">
            <Popover @triggerMode="hover">
              <:trigger as |pop|>
                <Chip
                  @intent="primary"
                  @size="sm"
                  {{pop.register}}
                  {{pop.hoverHandlers}}
                  tabindex="0"
                  class="cursor-help w-fit"
                >
                  <PhUserCheck @size="12" />
                  {{t "pslf.document_repo.submitted_on_your_behalf"}}
                </Chip>
              </:trigger>
              <:body>
                <div class="p-3 max-w-xs">
                  {{t "pslf.document_repo.submitted_on_your_behalf_tooltip"}}
                </div>
              </:body>
            </Popover>
            {{#if date}}
              <span class="text-sm tio-fg-muted">
                {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
              </span>
            {{/if}}
          </div>
        {{else if date}}
          {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
        {{else}}
          {{t "pslf.table.pending"}}
        {{/if}}
      </td>
    {{/let}}

    {{! Date Submitted to DOE }}
    <td class={{@cellClass}}>
      {{#if (eq (submittedToDoeDateDisplay @form) "date")}}
        {{safeFormatDate @form.submittedAt month="short" day="2-digit" year="numeric"}}
      {{else if (eq (submittedToDoeDateDisplay @form) "sent_by_mail")}}
        {{t "pslf.table.not_applicable"}}
        -
        {{t "pslf.table.print_and_mail"}}
      {{else if (eq (submittedToDoeDateDisplay @form) "pending")}}
        {{t "pslf.table.pending"}}
      {{else}}
        {{t "pslf.table.not_applicable"}}
      {{/if}}
    </td>

    {{! Employment Dates }}
    <td class={{@cellClass}}>
      {{#let @form.employmentStartDate @form.employmentEndDate as |startDate endDate|}}
        {{#if startDate}}
          {{safeFormatDate startDate month="short" day="2-digit" year="numeric"}}
          {{#if endDate}}
            -
            {{safeFormatDate endDate month="short" day="2-digit" year="numeric"}}
          {{else}}
            -
            {{t "pslf.reimagined.forms_table.present"}}
          {{/if}}
        {{else}}
          —
        {{/if}}
      {{/let}}
    </td>

    {{! Status }}
    <td class={{@cellClass}}>
      {{#if
        (or
          (eq @form.status "canceled") (eq @form.status "cancelled") (eq @form.status "submitted")
        )
      }}
        <Popover @triggerMode="hover">
          <:trigger as |pop|>
            <span tabindex="0" {{pop.register}} {{pop.hoverHandlers}}>
              {{t (concat "common.pslf.status." @form.status)}}
            </span>
          </:trigger>
          <:body>
            <div class="p-4">
              {{t (concat "pslf.status_tippy." @form.status) htmlSafe=true}}
            </div>
          </:body>
        </Popover>
      {{else}}
        {{t (concat "common.pslf.status." @form.status)}}
      {{/if}}
    </td>

    {{! Approver Info }}
    <td class={{@cellClass}}>
      {{pslfApproverDisplayEmail @form}}
    </td>

    {{! Form Type / Employer }}
    <td class={{@cellClass}}>
      <div class="truncate">
        {{t (concat "common.pslf.form-type." @form.formTypeDisplayKey)}}
        <br />
        {{@form.employerDisplayName}}
      </div>
    </td>

    {{! Actions }}
    <td class={{@cellClass}}>
      <div class="tio-flex-end">
        <Popover
          @placement="left"
          @isOpen={{eq @menuOpenId @form.id}}
          @onClose={{@onCloseMenu}}
          @maxWidth={{200}}
        >
          <:trigger as |p|>
            <button
              type="button"
              class="pslf-form-actions-trigger p-1 rounded border"
              {{p.register}}
              {{on "click" (fn @onToggleMenu @form.id)}}
              data-test-actions-trigger={{@form.id}}
              aria-label={{t "pslf.document_repo.row_actions"}}
              aria-haspopup="menu"
              aria-expanded={{eq @menuOpenId @form.id}}
            >
              <PhDotsThreeVertical @size="24" />
            </button>
          </:trigger>
          <:body>
            <div class="tio-col-flush py-1">
              {{#if @form.hasPdfPreview}}
                <button
                  type="button"
                  class="tio-menu-action tio-menu-action--compact"
                  {{on "click" (fn @onPreview @form)}}
                  data-test-preview={{@form.id}}
                >
                  <PhEye @size="16" />
                  {{t "pslf.document_repo.preview"}}
                </button>
              {{/if}}
              {{#if @form.canResendEmail}}
                <button
                  type="button"
                  class="tio-menu-action tio-menu-action--compact"
                  {{on "click" (fn @onAction "resend" @form)}}
                  data-test-resend={{@form.id}}
                >
                  <PhPaperPlaneTilt @size="16" />
                  {{t "pslf.document_repo.resend"}}
                </button>
              {{/if}}
              {{#if @form.canChangeApprover}}
                <button
                  type="button"
                  class="tio-menu-action tio-menu-action--compact"
                  {{on "click" (fn @onAction "changeApprover" @form)}}
                  data-test-change-approver={{@form.id}}
                >
                  <PhUserGear @size="16" />
                  {{t "pslf.document_repo.change_approver"}}
                </button>
              {{/if}}
              {{#if @form.canCancel}}
                <button
                  type="button"
                  class="tio-row-tight w-full px-3 py-2 text-sm tio-fg-danger hover:tio-bg-sunken rounded"
                  {{on "click" (fn @onAction "cancel" @form)}}
                  data-test-cancel={{@form.id}}
                >
                  <PhEyeSlash @size="16" />
                  {{t "disable"}}
                </button>
              {{/if}}
            </div>
          </:body>
        </Popover>
      </div>
    </td>
  </tr>
</template>;

// ---------------------------------------------------------------------------
// Main table component
// ---------------------------------------------------------------------------

export interface BorrowerFormsTableSignature {
  Args: {
    forms: PslfFormModel[];
  };
  Element: HTMLTableElement;
}

export default class PslfBorrowerFormsTable extends Component<BorrowerFormsTableSignature> {
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  @tracked showPreview = false;
  @tracked pdfSrc = '';
  @tracked formToChangeApprover?: PslfFormModel;
  @tracked showDisableConfirm = false;
  @tracked formToDisable?: PslfFormModel;
  @tracked formToResend?: PslfFormModel;
  @tracked formsLimit = INITIAL_LIMIT;
  @tracked menuOpenId: string | null = null;

  get sortedForms() {
    return this.args.forms
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  get visibleForms() {
    return this.sortedForms.slice(0, this.formsLimit);
  }

  get hasMoreForms() {
    return this.formsLimit < this.sortedForms.length;
  }

  get canShowLess() {
    return this.formsLimit > INITIAL_LIMIT;
  }

  showMoreForms = () => {
    this.formsLimit += INITIAL_LIMIT;
  };

  showLessForms = () => {
    this.formsLimit = INITIAL_LIMIT;
  };

  toggleMenu = (id: string | null) => {
    this.menuOpenId = this.menuOpenId === id ? null : id;
  };

  closeMenu = () => {
    this.menuOpenId = null;
  };

  doFormAction = (action: string, model: PslfFormModel) => {
    this.menuOpenId = null;
    if (action === 'changeApprover') {
      this.formToChangeApprover = model;
    }
    if (action === 'cancel') {
      this.formToDisable = model;
      this.showDisableConfirm = true;
    }
    if (action === 'resend') {
      this.resendEmployerEmail(model);
    }
  };

  openPreviewModal = (model: PslfFormModel) => {
    this.menuOpenId = null;
    setTimeout(() => {
      const url = model.attachments?.[0]?.signedUrl;
      this.pdfSrc = url || '';
      this.showPreview = true;
    }, 250);
  };

  closePreviewModal = () => {
    this.showPreview = false;
    this.pdfSrc = '';
  };

  disableSelectedForm = dropTask(async () => {
    const form = this.formToDisable;
    if (form) {
      // Legacy PslfFormAdapter#cancel PATCHed /cancel with X-Reindex-Mode: sync
      // then reloaded the form; preserve both steps.
      await this.store.request(cancelPslfFormRequest(form.id!));
      // /cancel goes through perform_action → `head 204` (renders NO resource),
      // so the builder does NOT refresh the cache — keep the reload via the helper.
      await reloadRecord(this.store, form);
    }
    this.showDisableConfirm = false;
  });

  cancelDisable = () => {
    delete this.formToDisable;
    this.showDisableConfirm = false;
  };

  async resendEmployerEmail(model: PslfFormModel) {
    const successMessage = this.intl.t('pslf.document_repo.resend_email_success', {
      email: model.approverDisplayEmail,
    });
    const errorMessage = this.intl.t('pslf.document_repo.resend_email_error', {
      email: model.approverDisplayEmail,
    });
    this.formToResend = model;
    try {
      // Legacy PslfFormAdapter#resendEmployerEmail PATCHed
      // /notify-employer-sign-reminder (NO X-Reindex-Mode header) then reloaded
      // the form; preserve both steps.
      await this.store.request(notifyEmployerSignReminderRequest(model.id!));
      // /notify-employer-sign-reminder goes through perform_action → `head 204`
      // (renders NO resource), so the builder does NOT refresh the cache — keep
      // the reload via the helper.
      await reloadRecord(this.store, model);
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (_err) {
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    } finally {
      delete this.formToResend;
    }
  }

  didChangeApprover = async (model: PslfFormModel) => {
    this.formToChangeApprover = undefined;
    if (model.canResendEmail) {
      await this.resendEmployerEmail(model);
    }
  };

  cancelChangeApprover = () => {
    this.formToChangeApprover = undefined;
  };

  <template>
    <Table @isLoading={{false}} ...attributes>
      <:thead as |options|>
        <options.tr>
          <options.th>{{t "pslf.document_repo.date_borrower_signed"}}</options.th>
          <options.th>{{t "pslf.reimagined.forms_table.date_submitted_to_doe"}}</options.th>
          <options.th>{{t "pslf.reimagined.forms_table.employment_dates"}}</options.th>
          <options.th>{{t "common.status"}}</options.th>
          <options.th>{{t "pslf.document_repo.approver_info"}}</options.th>
          <options.th>{{t "pslf.document_repo.form_type_and_employer"}}</options.th>
          <options.th class="whitespace-nowrap" />
        </options.tr>
      </:thead>
      <:tbody as |options|>
        {{#each this.visibleForms as |form|}}
          <BorrowerFormsRow
            @form={{form}}
            @cellClass={{options.cellClass}}
            @onPreview={{this.openPreviewModal}}
            @onAction={{this.doFormAction}}
            @menuOpenId={{this.menuOpenId}}
            @onToggleMenu={{this.toggleMenu}}
            @onCloseMenu={{this.closeMenu}}
          />
        {{else}}
          <options.tr>
            <options.td colspan="7">
              {{t "pslf.document_repo.no_forms_found"}}
            </options.td>
          </options.tr>
        {{/each}}
      </:tbody>
    </Table>

    {{#if (or this.hasMoreForms this.canShowLess)}}
      <div class="tio-flex-tight mt-2">
        {{#if this.hasMoreForms}}
          <Button
            @appearance="minimal"
            @size="sm"
            @icon={{PhCaretDown}}
            @label={{t "common.see_more"}}
            data-test-pslf-forms-see-more
            {{on "click" this.showMoreForms}}
          />
        {{/if}}
        {{#if this.canShowLess}}
          <Button
            @appearance="minimal"
            @size="sm"
            @icon={{PhCaretUp}}
            @label={{t "common.show_less"}}
            data-test-pslf-forms-show-less
            {{on "click" this.showLessForms}}
          />
        {{/if}}
      </div>
    {{/if}}

    <Modal
      @isOpen={{this.showPreview}}
      @onClose={{this.closePreviewModal}}
      @ariaLabel={{t "pslf.reimagined.forms_table.pdf_preview"}}
      class="min-h-64"
      @size="full"
    >
      <:body>
        {{#if this.pdfSrc}}
          <div class="w-full h-full max-w-4xl mx-auto">
            <div {{embedPdf this.pdfSrc}}></div>
          </div>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{this.closePreviewModal}}
          @submitText={{t "pslf.reimagined.forms_table.close_preview"}}
        />
      </:footer>
    </Modal>

    <ConfirmationDialog
      @isOpen={{this.showDisableConfirm}}
      @title={{t "pslf.document_repo.confirm_disable_title"}}
      @intent="danger"
      @onConfirm={{this.disableSelectedForm.perform}}
      @onCancel={{this.cancelDisable}}
      @isRunning={{this.disableSelectedForm.isRunning}}
      @confirmText={{t "disable"}}
    >
      <p>{{t "pslf.document_repo.confirm_disable_prompt_1"}}</p>
      <p class="font-semibold">
        {{t "pslf.document_repo.confirm_disable_prompt_2"}}
      </p>
    </ConfirmationDialog>

    {{#if this.formToChangeApprover}}
      <ChangeApprover
        @didCancel={{this.cancelChangeApprover}}
        @didSave={{this.didChangeApprover}}
        @form={{this.formToChangeApprover}}
      />
    {{/if}}
  </template>
}
